<template>
  <PagesForestPane :source="source" />
</template>
<script lang="ts">
import { defineComponent, ref, watch } from "vue";

import * as inject from "@/components/inject";
import type {
  PagesForestNodeRef,
  PagesForestSource,
} from "@/components/pages/pagesForest";
import PagesForestPane from "@/components/pages/PagesForestPane.vue";
import { personalPagesStorageKeys, usePages } from "@/composables/usePages";
import {
  ensureObjectNodeInForest,
  fetchPageParentNode,
  fetchPagesForest,
  flattenPagesHierarchies,
  flattenPageTitles,
  insertNodeIntoForest,
  movePageToParent,
  movePageWithinForest,
  pruneChildlessObjectNodes,
  removePageFromForest,
  updatePageTitlesInForest,
  type PagesForestNode,
} from "@/page";
import { useReleaser } from "@/ref";
import { RouteNames } from "@/routes/types";
import type { CustomObjectTarget } from "@/types";
import { navigateTo, navigateToObject } from "@/utils/navigate";

/**
 * The personal Pages screen's forest: the `PagesForestSource` over the native
 * `Page` RPCs — the forest comes from the extension rows' `_routine.parent`
 * pointers (see `fetchPagesForest`), moves go through `movePageToParent`
 * (pointer + Object blocks in the old/new parents' notes) — rendered with
 * the shared `PagesForestPane`.
 */
export default defineComponent({
  name: "PersonalPagesSource",
  components: { PagesForestPane },
  async setup() {
    const router = inject.router();
    const controller = inject.controller();
    const baseLogger = inject.logger();
    const logger = baseLogger.forModule("PersonalPagesSource");
    const contextMenu = inject.contextMenu();
    const profile = inject.profile();
    const releaser = useReleaser();

    const {
      addPage,
      openPage,
      release: releasePages,
    } = usePages(controller, router, profile, baseLogger);
    releaser.defer(releasePages);

    const forest = ref<PagesForestNode[]>([]);
    const changed = ref(0);
    const bump = () => {
      changed.value += 1;
    };

    // `null` resolves to the personal workspace in the controller. Only the
    // flat page set (and change ticks) come from here: the nesting is
    // rebuilt from the extension rows' `_routine.parent` pointers below —
    // the notes-host hierarchy, not the controller's native pages tree.
    const pages = await releaser.add(controller.page.all(null));

    // The forest (from `_routine.parent`) is computed in full exactly once,
    // on load: every page and its extension-row pointer. Afterwards it is
    // only *updated* — the source knows what changed (see `reconcile` and
    // `createPage` below) and never refetches the pointers of pages already
    // placed, which also preserves the drag & drop's local reordering.
    // Legacy native `page.parent` values are invisible here: the launch
    // migration (`repairPagesHierarchy`, fired from Routine.vue) rewrites
    // them as pointers — normally well before this pane mounts. A pane
    // racing that first migration shows the not-yet-migrated pages at the
    // root until its next mount.
    const first = await fetchPagesForest(
      controller,
      baseLogger,
      profile,
      flattenPagesHierarchies(pages.value ?? [])
    );
    forest.value = first.forest;

    // Forest updates are serialized on a queue so a pages tick and a
    // targeted update (e.g. `createPage`) never interleave; either order of
    // arrival converges to the same forest.
    let queue: Promise<void> = Promise.resolve();
    const enqueue = (op: () => Promise<void> | void) => {
      queue = queue.then(op).catch((error: unknown) => {
        logger.error("pages forest update failed", { error });
      });
    };

    // A `pages` tick means the flat page set (or a title) changed — created,
    // deleted, renamed. Diff it against the forest and apply the difference:
    // pointers are fetched only for the added pages.
    const reconcile = async () => {
      const flat = flattenPageTitles(pages.value ?? []);
      // Remove the *page* nodes that vanished from the flat set (object
      // nodes are not in it; the ones their pages left behind are pruned
      // below).
      const kinds = new Map<string, PagesForestNode["kind"]>();
      const collectKinds = (nodes: PagesForestNode[]): void => {
        for (const node of nodes) {
          kinds.set(node.id, node.kind);
          collectKinds(node.children);
        }
      };
      collectKinds(forest.value);
      for (const [id, kind] of kinds)
        if (kind === "page" && !flat.has(id))
          removePageFromForest(forest.value, id);
      const additions = await Promise.all(
        [...flat]
          .filter(([id]) => !kinds.has(id))
          .map(async ([id, title]) => ({
            id,
            title,
            parent: await fetchPageParentNode(controller, profile, id),
          }))
      );
      // Pages under other pages (or nothing): insert parents before their
      // children (a whole subtree can arrive in one tick); anything left in
      // a pointer cycle roots itself.
      let pageParented = additions.filter(
        ({ parent }) => parent === null || parent.kind === "page"
      );
      while (pageParented.length > 0) {
        const ready = pageParented.filter(
          ({ parent }) =>
            parent === null ||
            !pageParented.some((other) => other.id === parent.id)
        );
        for (const { id, title, parent } of ready.length > 0
          ? ready
          : pageParented)
          insertNodeIntoForest(
            forest.value,
            { id, kind: "page", title },
            parent?.id ?? null
          );
        pageParented =
          ready.length > 0
            ? pageParented.filter((addition) => !ready.includes(addition))
            : [];
      }
      // Pages hosted by a custom object: make sure the object has a node
      // (fetching its title, nesting it under its own parent when that
      // parent is in the forest), then file the page under it — at the root
      // when the object row is gone.
      for (const { id, title, parent } of additions) {
        if (parent?.kind !== "object") continue;
        const hosted = await ensureObjectNodeInForest(
          controller,
          forest.value,
          parent.id
        );
        insertNodeIntoForest(
          forest.value,
          { id, kind: "page", title },
          hosted ? parent.id : null
        );
      }
      updatePageTitlesInForest(forest.value, flat);
      pruneChildlessObjectNodes(forest.value);
      bump();
    };

    releaser.defer(
      watch(
        () => pages.value,
        () => enqueue(reconcile)
      )
    );

    // The + on an item (a page or a custom-object host) or the empty
    // state's button: no recomputation — we know exactly what changed, the
    // fresh page and its parent. `addPage` files the page under its parent
    // in the background, so the creation tick normally beats the
    // `_routine.parent` write and places the page at the root (the pointer
    // lands on the extension row, which never ticks `pages`); re-place it.
    // Root creations get the same immediate insert, so the page shows in
    // the pane without waiting on the `pages` tick's reconciliation.
    const createPage = async (parentId: string | null) => {
      const id = await addPage(parentId ?? undefined);
      enqueue(() => {
        // Whichever the racing tick did: re-place the node if it inserted
        // one, insert it otherwise (each is a no-op in the other case).
        movePageWithinForest(forest.value, id, parentId);
        insertNodeIntoForest(
          forest.value,
          { id, kind: "page", title: "" },
          parentId
        );
        bump();
      });
      return id;
    };

    // A drag & drop move reported by the pane: persist it — rewrite the
    // `_routine.parent` pointer on the page's extension row and the Object
    // blocks in the old/new parents' notes (`movePage` in `@/page`; the
    // destination may be a page or a custom object) — then re-place the
    // node on the *persisted* parent: the move may have been refused
    // (destination inside the dragged page's own subtree) or failed, and
    // the drag's optimistic splices only touched the pane's disposable
    // displayed copy, never this forest (see `getHierarchy`).
    const movePage = (id: string, parentId: string | null) => {
      enqueue(async () => {
        try {
          await movePageToParent(controller, baseLogger, profile, id, parentId);
        } catch (error) {
          logger.error("persisting the drag move failed", {
            pageId: id,
            parentId,
            error,
          });
        }
        const parent = await fetchPageParentNode(controller, profile, id);
        movePageWithinForest(forest.value, id, parent?.id ?? null);
        bump();
      });
      return Promise.resolve();
    };

    // Previewing either kind goes through the pages view's `id` query (the
    // route renders the matching preview), but only pages record a "last
    // open page".
    const preview = (
      node: PagesForestNodeRef | null,
      event?: MouseEvent | KeyboardEvent
    ) => {
      if (node === null) return openPage(null, true);
      if (node.kind === "object")
        return navigateTo(
          RouteNames.OrgPages,
          router,
          {
            params: { orgId: profile.value.user.personal.organizationId },
            query: { id: node.id },
          },
          event
        );
      return openPage(node.id, true, event);
    };

    const openFullScreen = (
      node: PagesForestNodeRef,
      event?: MouseEvent | KeyboardEvent
    ) =>
      node.kind === "object"
        ? navigateToObject(
            router,
            controller,
            { kind: "object", id: node.id },
            event
          )
        : openPage(node.id, false, event);

    const showContextMenu = async (
      node: PagesForestNodeRef,
      event: MouseEvent
    ) => {
      if (node.kind === "object") {
        // A custom-object node: its id is the full graph object id — the
        // context menu takes the raw {workspace, table, row} triple.
        const raw = await controller.graph.object.id.parse(node.id);
        const target: CustomObjectTarget = { type: "object", ...raw };
        contextMenu.value.show({ type: "object", value: target }, event);
        return;
      }
      const data = await controller.page.get(node.id);
      data.release();
      // FIXME: loading state if the page is not yet loaded
      // FIXME: expecting the whole object data to display a
      // contextual menu is excessive
      if (data.value)
        contextMenu.value.show({ type: "page", value: data.value }, event);
    };

    const source: PagesForestSource = {
      forest,
      changed,
      // `pages/<orgId>/<workspaceId>/…`, like the team panes' keys.
      ...personalPagesStorageKeys(profile),
      createPage,
      movePage,
      preview,
      openFullScreen,
      showContextMenu,
    };

    return { source };
  },
});
</script>
